import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { CenteredModal } from '~/components';
import permissionRequestApi from '~/api/permissionRequestApi';
import { faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import '~/pages/authentications/auth.css';

export default function UnauthorizedAdminAccess({ 
    title = "Access Denied", 
    message = "You do not have the required permissions to access this feature. Please contact your system administrator if you believe this is an error.",
    requiredPermission = null,
    moduleName = "this screen"
}) {
    const [showModal, setShowModal] = useState(false);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const handleRequestAccess = async () => {
        setErrorMsg("");
        setSuccessMsg("");
        
        if (!reason.trim()) {
            setErrorMsg("Please provide a reason for requesting access.");
            return;
        }

        setSubmitting(true);
        try {
            const res = await permissionRequestApi.createRequest({
                RequestedPermission: requiredPermission,
                Reason: reason
            });
            if (res.data?.succeeded || res.data?.Succeeded) {
                setSuccessMsg(res.data.message || res.data.Message || "Request submitted successfully.");
                setTimeout(() => {
                    setShowModal(false);
                    setReason("");
                    setSuccessMsg("");
                }, 5000);
            } else {
                setErrorMsg(res.data?.message || res.data?.Message || "Failed to submit request.");
            }
        } catch (err) {
            setErrorMsg("An error occurred while submitting the request.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        if (!submitting) {
            setShowModal(false);
            setErrorMsg("");
            setSuccessMsg("");
            setReason("");
        }
    };
    return (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center" style={{ minHeight: '400px', width: '100%' }}>
            <div 
                className="mb-4 d-flex align-items-center justify-content-center rounded-circle shadow-sm" 
                style={{ 
                    width: '80px', 
                    height: '80px', 
                    background: 'linear-gradient(135deg, #fff3cd, #f8d7da)', 
                    color: '#dc3545',
                    border: '1px solid #f5c2c7'
                }}
            >
                <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: '2.5rem' }} />
            </div>
            <h4 className="fw-bolder text-body-emphasis mb-2">{title}</h4>
            <p className="text-muted fs-8-1" style={{ maxWidth: '450px', lineHeight: '1.6' }}>{message}</p>
            
            {requiredPermission && (
                <button 
                    className="btn btn-primary mt-3 px-4 rounded-5"
                    onClick={() => setShowModal(true)}
                >
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                    Request Access
                </button>
            )}

            <CenteredModal
                show={showModal}
                title="Request Access"
                onClose={handleCloseModal}
                onConfirm={handleRequestAccess}
                isSubmitting={submitting}
                confirmText="Submit Request"
                isConfirmDisabled={!reason.trim() || successMsg !== ""}
            >
                <div className="p-3">
                    {errorMsg && (
                        <div className="auth-error-message mb-3">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <span>{errorMsg}</span>
                        </div>
                    )}
                    {successMsg && (
                        <div className="auth-success-message mb-3">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <span>{successMsg}</span>
                        </div>
                    )}
                    <p className="fs-8-1 text-muted mb-3">
                        You are requesting access to <strong>{moduleName}</strong> (Permission: <code>{requiredPermission}</code>).
                        <br />
                        <span className="text-info fw-bold mt-2 d-inline-block">
                            Your request will be sent to the Super Administrator for review. Once approved, you will be able to access this feature.
                        </span>
                    </p>
                    <div className="mb-3">
                        <label className="form-label fs-8-1 fw-bold text-body-emphasis">
                            Reason for Access <span className="text-danger">*</span>
                        </label>
                        <textarea 
                            className="form-control"
                            rows={3} 
                            placeholder="Explain why you need access to this feature..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={submitting}
                        ></textarea>
                    </div>
                </div>
            </CenteredModal>
        </div>
    );
}
